// Pure transforms from the /api/sports/catalog payload into view-model shapes.
// No I/O — fetching lives in context/CatalogContext.js.

// Minimal football-only fallback so existing athletes are never locked out if
// the catalog fetch fails. Measurables mirror the fields PlayerStatsPage had.
export const FOOTBALL_FALLBACK = {
  sports: [{
    slug: 'football', name: 'Football', position_noun: 'Position',
    has_positions: true, score_supported: true,
    positions: [
      { slug: 'qb', label: 'Quarterback', group: 'Offense' },
      { slug: 'rb', label: 'Running Back', group: 'Offense' },
      { slug: 'wr', label: 'Wide Receiver', group: 'Offense' },
      { slug: 'te', label: 'Tight End', group: 'Offense' },
      { slug: 'ot', label: 'Offensive Tackle', group: 'Offense' },
      { slug: 'de', label: 'Defensive End', group: 'Defense' },
      { slug: 'dt', label: 'Defensive Tackle', group: 'Defense' },
      { slug: 'olb', label: 'Outside Linebacker', group: 'Defense' },
      { slug: 'cb', label: 'Cornerback', group: 'Defense' },
      { slug: 'fs', label: 'Free Safety', group: 'Defense' },
      { slug: 'k', label: 'Kicker', group: 'Special Teams' },
      { slug: 'ath', label: 'Athlete/Utility', group: 'Other' },
    ],
    measurables: [
      { key: 'height_inches', label: 'Height', unit: 'in', value_type: 'decimal', precision: 0.1, min: 48, max: 96, lower_is_better: false, enum_options: null, category: 'Size' },
      { key: 'weight_lbs', label: 'Weight', unit: 'lb', value_type: 'decimal', precision: 0.1, min: 60, max: 400, lower_is_better: false, enum_options: null, category: 'Size' },
      { key: 'wingspan', label: 'Wingspan', unit: 'in', value_type: 'decimal', precision: 0.1, min: 48, max: 96, lower_is_better: false, enum_options: null, category: 'Size' },
      { key: 'hand_size', label: 'Hand Size', unit: 'in', value_type: 'decimal', precision: 0.25, min: 6, max: 12, lower_is_better: false, enum_options: null, category: 'Size' },
      { key: 'dash_40', label: '40-Yard Dash', unit: 's', value_type: 'decimal', precision: 0.01, min: 3.9, max: 12.99, lower_is_better: true, enum_options: null, category: 'Speed' },
      { key: 'short_shuttle', label: 'Short Shuttle (5-10-5)', unit: 's', value_type: 'decimal', precision: 0.01, min: 3.5, max: 6.5, lower_is_better: true, enum_options: null, category: 'Agility' },
      { key: 'three_cone_drill', label: '3-Cone Drill', unit: 's', value_type: 'decimal', precision: 0.01, min: 6, max: 9, lower_is_better: true, enum_options: null, category: 'Agility' },
      { key: 'vertical_jump', label: 'Vertical Jump', unit: 'in', value_type: 'decimal', precision: 0.1, min: 10, max: 50, lower_is_better: false, enum_options: null, category: 'Power' },
      { key: 'broad_jump', label: 'Broad Jump', unit: 'in', value_type: 'decimal', precision: 0.1, min: 60, max: 150, lower_is_better: false, enum_options: null, category: 'Power' },
      { key: 'bench_press', label: 'Bench Press', unit: 'lb', value_type: 'integer', precision: 1, min: 45, max: 600, lower_is_better: false, enum_options: null, category: 'Strength' },
      { key: 'squat', label: 'Squat', unit: 'lb', value_type: 'integer', precision: 1, min: 45, max: 900, lower_is_better: false, enum_options: null, category: 'Strength' },
    ],
  }],
};

export function normalizeCatalog(raw) {
  if (!raw || !Array.isArray(raw.sports) || raw.sports.length === 0) {
    return FOOTBALL_FALLBACK;
  }
  return raw;
}

// Every sport dropdown reads this, so sorting here keeps them all alphabetical. The API's own order
// is the catalog's registration order and carries no meaning for the user.
export const byName = (a, b) => a.name.localeCompare(b.name);

export function sportsList(catalog) {
  return (catalog.sports || []).map(s => ({
    slug: s.slug, name: s.name, positionNoun: s.position_noun,
    hasPositions: s.has_positions, scoreSupported: s.score_supported,
  })).sort(byName);
}

export function getSport(catalog, slug) {
  return (catalog.sports || []).find(s => s.slug === slug);
}

export function positionsFor(catalog, slug) {
  const sport = getSport(catalog, slug);
  if (!sport) return [];
  return (sport.positions || []).map(p => ({ slug: p.slug, label: p.label, group: p.group }));
}

export function groupedPositionsFor(catalog, slug) {
  const out = [];
  const index = {};
  for (const p of positionsFor(catalog, slug)) {
    const key = p.group || '';
    if (!(key in index)) { index[key] = out.length; out.push({ group: p.group, positions: [] }); }
    out[index[key]].positions.push(p);
  }
  return out;
}

export function measurablesFor(catalog, slug) {
  const sport = getSport(catalog, slug);
  if (!sport) return [];
  return (sport.measurables || []).map(m => ({
    key: m.key, label: m.label, unit: m.unit, valueType: m.value_type,
    precision: m.precision, min: m.min, max: m.max,
    lowerIsBetter: m.lower_is_better, enumOptions: m.enum_options, category: m.category,
  }));
}

// Per-sport ranking/rating services (e.g. Perfect Game, TopDrawerSoccer).
// Returns [] for sports with no star-style service (cheer, equestrian) or when
// the catalog is in football fallback (no ranking_services key).
export function rankingServicesFor(catalog, slug) {
  const sport = getSport(catalog, slug);
  if (!sport) return [];
  return (sport.ranking_services || []).map(r => ({ key: r.key, label: r.label }));
}

// The web profile only knows an athlete's sport as a display name string
// (profile.sport, e.g. "Soccer"); the catalog is keyed by slug. Reverse-derive
// the slug once here instead of copy-pasting the lookup at every call site.
export function sportSlugFor(catalog, sportName) {
  return ((catalog.sports || []).find(s => s.name === sportName) || {}).slug || 'football';
}

// Routes the Stats screen: "game_stats" | "event_marks" | "result_entries" |
// "match_record". Defaults to "game_stats" so sports without the field (or the
// football fallback) still route correctly.
export function statsModeFor(catalog, slug) {
  const sport = getSport(catalog, slug);
  return (sport && sport.stats_mode) || 'game_stats';
}

// Fixed pathway reference data (e.g. equestrian's NCEA/IHSA structure) surfaced
// on the sport dict as `pathways`. normalizeCatalog passes the raw payload
// through unchanged, so the field survives for sports that define it; returns
// null for sports without pathways or in football fallback.
export function pathwaysFor(catalog, slug) {
  const sport = getSport(catalog, slug);
  return (sport && sport.pathways) || null;
}

export function groupedMeasurablesFor(catalog, slug) {
  const out = [];
  const index = {};
  for (const m of measurablesFor(catalog, slug)) {
    const key = m.category || '';
    if (!(key in index)) { index[key] = out.length; out.push({ category: m.category, measurables: [] }); }
    out[index[key]].measurables.push(m);
  }
  return out;
}
